import { useAuth0 } from '@auth0/auth0-react'
import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar, MenuList, MenuListDivider, Profile, Tooltip } from '@clickguard/design-system'
import type { HeaderPopover } from 'layout/navbar/notifications/types'
import { AppConfig } from 'config'
import { ChatService } from 'services/chat'
import { useStores } from 'hooks'
import { useWorkspaceAccess } from 'hooks/use-workspace-access'
import { removeFromLocalStorage } from 'utils'
import { clearMarketingParams } from 'utils/marketing-params'
import { clearFiltersFromStorage } from 'utils/filter-presistence'
import Sale03 from '@untitled-ui/icons-react/build/esm/Sale03'
import MessageCircle01 from '@untitled-ui/icons-react/build/esm/MessageCircle01'
import LinkExternal01 from '@untitled-ui/icons-react/build/esm/LinkExternal01'
import CurrencyDollarCircle from '@untitled-ui/icons-react/build/esm/CurrencyDollarCircle'
import Speedometer02 from '@untitled-ui/icons-react/build/esm/Speedometer02'
import LogOut03 from '@untitled-ui/icons-react/build/esm/LogOut03'
import PuzzlePiece01 from '@untitled-ui/icons-react/build/esm/PuzzlePiece01'

const CHROME_EXTENSION_URL =
	'https://chromewebstore.google.com/detail/mlfckffanpidplofdehopinomopjkmem'

interface UserActionsOverflowProps {
	removeImpersonationId: () => void
	headerPopover: HeaderPopover
	setHeaderPopover: (v: HeaderPopover) => void
}

const MenuItem = ({
	icon,
	label,
	onClick
}: {
	icon: React.ReactNode
	label: string
	onClick: () => void
}) => (
	<button
		onClick={onClick}
		className="flex items-center gap-[8px] w-full min-h-[36px] p-[8px] rounded-[10px] hover:bg-t-step-inactive transition-colors cursor-pointer"
	>
		<span className="flex items-center justify-center shrink-0 size-[20px] text-t-coupon">
			{icon}
		</span>
		<span className="text-[14px] leading-[18px] text-t-coupon truncate">
			{label}
		</span>
	</button>
)

export const UserActionsOverflow = ({
	removeImpersonationId,
	headerPopover,
	setHeaderPopover
}: UserActionsOverflowProps) => {
	const { logout } = useAuth0()
	const navigate = useNavigate()
	const { userStore, billingStore, accountStore } = useStores()
	const { user } = userStore
	const showOffersBadge = billingStore.offers?.length > 0
	// Case 3 (no ad account) and Case 6 (cancelled) hide the documentation link.
	const { access } = useWorkspaceAccess()

	const open = headerPopover === 'profile'
	const setOpen = (v: boolean) => setHeaderPopover(v ? 'profile' : null)
	const containerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!open) return
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false)
			}
		}
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [open])

	const handleLogout = () => {
		removeFromLocalStorage(['selected_account', 'selected_asset'])
		clearFiltersFromStorage()
		clearMarketingParams()
		removeImpersonationId()
		logout()
	}

	const profileTrigger = (
		<Profile
			size="md"
			tone="purple-solid"
			name={user?.name || undefined}
			src={user?.avatar || undefined}
			selected={open}
			aria-label="Profile"
			onClick={() => setOpen(!open)}
		/>
	)

	return (
		<div className="relative h-10 flex items-center" ref={containerRef}>
			{/* Drop the hover tooltip while the profile dropdown is open so it
			   doesn't linger over the menu (review pin #0029). The DS Tooltip is
			   hover-only with no controlled-open prop, so we unwrap instead.
			   Gated on this menu's own `open` (not the shared headerPopover): the
			   avatar lives inside its own container, so toggling it never
			   remounts the trigger mid-click. */}
			{open ? (
				profileTrigger
			) : (
				<Tooltip content="Profile" position="bottom">
					{profileTrigger}
				</Tooltip>
			)}

			{open && (
				<div className="absolute right-0 top-full mt-2 z-50">
					<MenuList className="w-[320px]">
						{/* User info header */}
						<div className="flex items-center gap-[12px] py-[8px] rounded-[16px]">
							<Avatar
								size="lg"
								tone="purple-solid"
								name={user?.name || undefined}
								src={user?.avatar || undefined}
							/>
							<div className="flex flex-col min-w-0 flex-1">
								<span className="text-[16px] font-medium leading-[22px] text-t-coupon truncate">
									{user?.name}
								</span>
								<span className="text-[14px] leading-[18px] text-t-desc-gray truncate">
									{user?.email}
								</span>
							</div>
						</div>

						<MenuListDivider />

						{/* Offers */}
						<MenuItem
							icon={
								<span className="relative inline-block size-[20px] leading-none">
									<Sale03 width={20} height={20} />
									{showOffersBadge && (
										<span
											aria-hidden="true"
											className="absolute top-0 right-0 w-[6px] h-[6px] rounded-[3px] bg-[#F04438] pointer-events-none"
										/>
									)}
								</span>
							}
							label="Offers"
							onClick={() => {
								navigate('/settings/user/offers')
								setOpen(false)
							}}
						/>

						{/* Get browser extension */}
						<MenuItem
							icon={<PuzzlePiece01 width={20} height={20} />}
							label="Get browser extension"
							onClick={() => {
								window.open(CHROME_EXTENSION_URL, '_blank', 'noopener,noreferrer')
								setOpen(false)
							}}
						/>

						{/* Affiliate program */}
						<MenuItem
							icon={<CurrencyDollarCircle width={20} height={20} />}
							label="Affiliate program"
							onClick={() => {
								navigate('/workspace/affiliate?tab=about')
								setOpen(false)
							}}
						/>

						{/* Chat with us */}
						<MenuItem
							icon={<MessageCircle01 width={20} height={20} />}
							label="Chat with us"
							onClick={() => {
								ChatService.open()
								setOpen(false)
							}}
						/>

						{/* See documentation */}
						{access !== 'no-ad-account' && access !== 'cancelled' && (
							<MenuItem
								icon={<LinkExternal01 width={20} height={20} />}
								label="See documentation"
								onClick={() => {
									window.open(AppConfig.KNOWLEDGEBASE_URL, '_blank')
									setOpen(false)
								}}
							/>
						)}

						{/* Admin Panel — CG users only */}
						{user?.admin && (
							<>
								<MenuListDivider />
								<MenuItem
									icon={<Speedometer02 width={20} height={20} />}
									label="Admin Panel"
									onClick={() => {
										accountStore.clearAccount()
										navigate('/admin')
										setOpen(false)
									}}
								/>
							</>
						)}

						<MenuListDivider />

						{/* Logout */}
						<MenuItem
							icon={<LogOut03 width={20} height={20} />}
							label="Logout"
							onClick={handleLogout}
						/>
					</MenuList>
				</div>
			)}
		</div>
	)
}
